import { useState, useEffect } from 'react';
import { MessageSquare, Calendar, Filter, Star, Mail, User } from 'lucide-react';
import apiClient from '../api/client';
import { FeedbackDto } from '../types/dtos';

export default function UserFeedback() {
    const [feedback, setFeedback] = useState<FeedbackDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState('All');

    useEffect(() => {
        fetchFeedback();
    }, []);

    const fetchFeedback = async () => {
        try {
            const res = await apiClient.get<FeedbackDto[]>('/admin/feedback');
            setFeedback(res.data);
        } catch (err: any) {
            console.error('Failed to load feedback:', err);
            setError(err.response?.data?.message || 'Failed to load feedback content.');
        } finally {
            setLoading(false);
        }
    };

    const filteredFeedback = filter === 'All'
        ? feedback
        : feedback.filter(f => f.type === filter);

    const getRatingColor = (rating: number) => {
        if (rating >= 4) return 'text-green-500';
        if (rating === 3) return 'text-yellow-500';
        return 'text-red-500';
    };

    return (
        <div className="w-full">
            <div className="max-w-7xl mx-auto pt-6 pb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">User Feedback</h1>
                                <p className="text-gray-500 text-sm mt-1">
                                    View and manage feedback submitted by customers
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                            {/* Filters */}
                            <div className="p-4 border-b border-gray-200 flex items-center gap-4 overflow-x-auto">
                                <div className="flex items-center gap-2 text-gray-500 text-sm font-medium">
                                    <Filter size={16} />
                                    <span>Filter by:</span>
                                </div>
                                {['All', 'General', 'Bug Report', 'Feature Request', 'Service Complaint'].map((type) => (
                                    <button
                                        key={type}
                                        onClick={() => setFilter(type)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${filter === type
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>

                            {loading ? (
                                <div className="p-8 text-center text-gray-500">Loading feedback...</div>
                            ) : error ? (
                                <div className="p-12 text-center text-red-600 font-medium whitespace-pre-wrap">
                                    {error}
                                </div>
                            ) : filteredFeedback.length === 0 ? (
                                <div className="p-12 text-center text-gray-500">
                                    <MessageSquare size={48} className="mx-auto mb-4 text-gray-300" />
                                    <p>No feedback found matching your criteria.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {filteredFeedback.map((item) => (
                                        <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.type === 'Bug Report' ? 'bg-red-100 text-red-800' :
                                                            item.type === 'Feature Request' ? 'bg-purple-100 text-purple-800' :
                                                                item.type === 'Service Complaint' ? 'bg-orange-100 text-orange-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                            }`}>
                                                            {item.type}
                                                        </span>
                                                        <span className="text-xs text-gray-400 flex items-center gap-1">
                                                            <Calendar size={12} />
                                                            {new Date(item.createdAt).toLocaleDateString()}
                                                        </span>
                                                    </div>

                                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.subject}</h3>
                                                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">{item.message}</p>

                                                    <div className="flex items-center gap-6 text-sm text-gray-500">
                                                        <div className="flex items-center gap-2">
                                                            <User size={16} />
                                                            <span>{item.customerName}</span>
                                                        </div>
                                                        {item.customerEmail && (
                                                            <div className="flex items-center gap-2">
                                                                <Mail size={16} />
                                                                <a href={`mailto:${item.customerEmail}`} className="hover:text-blue-600">
                                                                    {item.customerEmail}
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-1 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm">
                                                    <span className={`font-bold ${getRatingColor(item.rating)}`}>{item.rating}.0</span>
                                                    <div className="flex">
                                                        {[...Array(5)].map((_, i) => (
                                                            <Star
                                                                key={i}
                                                                size={12}
                                                                className={i < item.rating ? getRatingColor(item.rating) : 'text-gray-300'}
                                                                fill={i < item.rating ? 'currentColor' : 'none'}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                </div>
            </div>
        </div>
    );
}
